import * as React from 'react';
import { useMutation, useQueries, useQuery } from 'react-query';
import { getPatientStats } from 'pages/Dashboard/services/freeTrial.service';
import { Box, debounce, Pagination } from '@mui/material';
import { useHttp } from 'hooks/use-fetch';
import useSearchParamsState from 'hooks/use-search-params-state';
import ListPatientFilters from 'pages/Dashboard/components/ListPatientFilters';
import ListPatients from 'pages/Dashboard/components/ListPatientNew';
import { RefetchPatientsCtx } from 'pages/Dashboard/constants';
import { PATIENT_LIST_VIEW } from 'pages/Dashboard/enums';
import usePatientList from 'pages/Dashboard/hooks/usePatientList';
import {
  fetchProvidersAssociationsList,
  fetchUserMetadataWithKeys,
  postUserMetadata,
} from 'pages/Dashboard/services/provider.services';
import { providers } from 'utils/apiUrls';
import { UserMetadata, UserMetadataFlags } from 'pages/Dashboard/types';
import { PracticeProvider } from 'pages/Dashboard/types/providers.types';
import { useUnifiedAuth } from 'components/Common/UnifiedAuthContext';
import { logSentryInfo, trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import { StepModal } from 'pages/OnboardingFlow/OnboardingStepModal';
import EditGoalDrawer from 'pages/Dashboard/EditGoalDrawer';
import DashboardStatsCard from 'pages/Dashboard/components/DashboardStatsCard';


const rowsPerPage = 25;
const defaultSortField = 'patientActivity';
const SORT_FIELD = {
  [PATIENT_LIST_VIEW.READY]: defaultSortField,
  [PATIENT_LIST_VIEW.PENDING]: 'dateAdded',
};
const sortDirection = 'desc';

export default function DashboardV2() {
  const {
    http,
    providerId,
    addPatientGoal: addPatientGoalFromContext,
    setAddPatientGoal,
  } = useHttp();
  const { user } = useUnifiedAuth();

  const [statusFilter, setStatusFilter] = useSearchParamsState('status', PATIENT_LIST_VIEW.READY);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [page, setPage] = React.useState(1); // 1-based for MUI Pagination
  const [providerIdFilter, setProviderIdFilter] = React.useState<string>('');
  const handleProviderIdFilterChange = (provider: string) => {
    setProviderIdFilter(provider);
    trackMixpanelEvent(MixpanelEventName.DASHBOARD_PROVIDER_DROPDOWN_FILTER_SELECTED, {
      selectedProviderId: provider,
    });
  };

  // StepModal state
  const [showStepModal, setShowStepModal] = React.useState(false);
  const [stepModalStep, setStepModalStep] = React.useState(0);

  const [showEditGoalDrawer, setShowEditGoalDrawer] = React.useState(false);

  const [isOnboarded, setIsOnboarded] = React.useState('true');

  const sortField = SORT_FIELD[statusFilter as PATIENT_LIST_VIEW] ?? defaultSortField;

  const filters = React.useMemo(
    () => ({
      status: statusFilter,
    }),
    [statusFilter],
  );

  useQueries([
    {
      queryKey: [providerId, UserMetadataFlags.IsOnboardedFlag],
      queryFn: fetchUserMetadataWithKeys(http.post),
      onSuccess: (data: UserMetadata) => {
        setIsOnboarded(data.flag.value ?? '');
      },
      onError: () => {
        setIsOnboarded('true');
      },
      retry: 0,
    },
  ]);

  const {
    isFetching,
    isError,
    filteredPatientList,
    patientsCount,
    demoPatientsCount,
    isLoadingPatientsList,
    refetchPatientsList,
    refetchPatientsCount,
  } = usePatientList(
    providerId,
    filters,
    null,
    searchQuery,
    sortDirection,
    sortField,
    (page - 1) * rowsPerPage,
    rowsPerPage,
    providerIdFilter as string,
  );

  const setUserMetadataMutation = useMutation({
    mutationFn: (payload: UserMetadata) => postUserMetadata(http.post, { ...payload }),
  });

  const setAddPatientPreference = React.useCallback(
    (name: UserMetadataFlags, val: string) => {
      setUserMetadataMutation.mutate({
        userId: providerId,
        flag: {
          name,
          value: val,
        },
      });
    },
    [setUserMetadataMutation, providerId],
  );

  const providerAssociations = useQuery(
    [`${providers.providerAssociationList.queryUrl}`, providerId, 'dashboard'],
    {
      queryFn: fetchProvidersAssociationsList(http.get),
    },
  );

  // Always reset step to 0 when opening StepModal via info icon
  const openStepModal = React.useCallback(() => {
    setStepModalStep(0);
    setShowStepModal(true);
  }, []);

  const updateStatusFilter = React.useCallback(
    (updatedStatusFilter: string) => {
      setPage(1);
      setStatusFilter(updatedStatusFilter);
      trackMixpanelEvent(MixpanelEventName
        .DASHBOARD_STATUS_TAB_FILTER_SELECTED, { status: updatedStatusFilter });
    },
    [setStatusFilter],
  );
  React.useEffect(() => {
    logSentryInfo('Dashboard_loaded');
  }, []);

  const refetchPatients = React.useCallback(() => {
    refetchPatientsList();
    refetchPatientsCount();
  }, [refetchPatientsList, refetchPatientsCount]);

  const refetchPatientsCtx = React.useMemo(
    () => ({
      refetchPatients,
    }),
    [refetchPatients],
  );

  const handleSearch = (value: string) => {
    setPage(1);
    setSearchQuery(value);
    trackMixpanelEvent(MixpanelEventName.DASHBOARD_SEARCH_BY_NAME_SEARCHED, {});
  };

  React.useEffect(() => {
    if (sortField && sortDirection) {
      trackMixpanelEvent(MixpanelEventName
        .DASHBOARD_SORTABLE_TABLE_HEADER_APPLIED, { sortField, sortDirection });
    }
  }, [sortField]);

  const debouncedSearchQuery = debounce(handleSearch, 500);
  const providerIdFilterList = React.useMemo(() => {
    if (
      providerAssociations.data?.providers?.length
      && providerAssociations.data.providers.length > 0
    ) {
      const youOption: PracticeProvider = {
        id: providerId,
        firstName: `${user?.name} (You)`,
        lastName: '',
        npiNumber: '',
        designation: '',
        email: '',
      };

      const sortedProviders = [...providerAssociations.data.providers].sort((a, b) => {
        const lastNameA = a.lastName?.toLowerCase() ?? '';
        const lastNameB = b.lastName?.toLowerCase() ?? '';
        return lastNameA.localeCompare(lastNameB);
      });

      return [youOption, ...sortedProviders];
    }
    else {
      return [];
    }
  }, [providerAssociations, providerId, user?.name]);

  // Show onboarding modal by default only once after silent login
  React.useEffect(() => {
    if (isOnboarded === 'false') {
      setStepModalStep(0);
      setShowStepModal(true);
    }
  }, [isOnboarded, showStepModal]);

  // Fetch patient stats for provider (must be before any early returns)
  const {
    data: patientStats,
    isLoading: isPatientStatsLoading,
  } = useQuery(
    ['patientStats', providerId],
    () => getPatientStats(http.get, providerId),
    { enabled: !!providerId },
  );

  const activatedPatients = (patientsCount?.[`${PATIENT_LIST_VIEW.READY}-initial`] ?? 0)
    - (demoPatientsCount.READY ?? 0) as number;
  const pendingPatients = patientsCount?.[`${PATIENT_LIST_VIEW.PENDING}-initial`] ?? 0;


  const numWithHealthRecords = (patientStats?.noOfPatientsWithHealthRecords ?? 0)
   - (demoPatientsCount.READY ?? 0);
  const percentageWithHealthRecords = activatedPatients
    > 0 ? Math.round((numWithHealthRecords / activatedPatients) * 100) : 0;
  const numWithpercentageEngaged = (patientStats?.noOfPatientsEngaged ?? 0)
   - (demoPatientsCount.READY ?? 0);
  const percentageWithPercentageEngaged = activatedPatients
    > 0 ? Math.round((numWithpercentageEngaged / activatedPatients) * 100) : 0;
  const addPatientGoal = Number(addPatientGoalFromContext) || 0;
  const showProgressBar = (providerIdFilterList.length <= 0
     || providerId === providerIdFilter)
     && addPatientGoalFromContext && Number(addPatientGoalFromContext) !== 0
     && isOnboarded === 'true' && !providerAssociations.isLoading;
  const isLoadingProgressBarData = isPatientStatsLoading || (
    isLoadingPatientsList && filters.status === PATIENT_LIST_VIEW.READY
    && patientsCount?.[PATIENT_LIST_VIEW.READY] === undefined
  );

  return (
    <RefetchPatientsCtx.Provider value={refetchPatientsCtx}>
      <StepModal
        open={showStepModal}
        step={stepModalStep}
        onNext={() => {
          if (stepModalStep < 3) {
            setStepModalStep(stepModalStep + 1);
          }
          else {
            setShowStepModal(false);
          }
        }}
        onClose={() => {
          setShowStepModal(false);
          if (isOnboarded === 'false') {
            setIsOnboarded('true');
            setAddPatientPreference(UserMetadataFlags.IsOnboardedFlag, 'true');
          }
        }}
        totalSteps={4}
      />
      {showProgressBar && (
      <>
        <EditGoalDrawer
          isOpen={showEditGoalDrawer}
          closeDrawer={() => setShowEditGoalDrawer(false)}
          initialGoal={addPatientGoal}
          onSave={(newGoal) => {
            setAddPatientGoal(newGoal.toString());
            setShowEditGoalDrawer(false);
          }}
          leastGoal={Math.max(activatedPatients + pendingPatients, 1)}
        />
        <DashboardStatsCard
          isPatientStatsLoading={isLoadingProgressBarData}
          openStepModal={openStepModal}
          setShowEditGoalDrawer={setShowEditGoalDrawer}
          addPatientGoal={addPatientGoal}
          activatedPatients={activatedPatients}
          pendingPatients={pendingPatients}
          percentageWithHealthRecords={percentageWithHealthRecords}
          percentageWithPercentageEngaged={percentageWithPercentageEngaged}
        />
      </>
      )}
      <Box
        sx={{
          mx: 'auto',
          width: '100%',
          mt: showProgressBar ? 3 : 0,
        }}
      >
        <ListPatientFilters
          statusFilter={statusFilter}
          patientsCount={patientsCount}
          updateStatusFilter={updateStatusFilter}
          providerAssociations={providerIdFilterList}
          setProviderIdFilter={handleProviderIdFilterChange}
          providerIdFilter={providerIdFilter ?? ''}
          debouncedSearchQuery={debouncedSearchQuery}
        />
        <ListPatients
          filteredPatientList={filteredPatientList}
          isError={isError}
          isFetching={isFetching}
          statusFilter={statusFilter as PATIENT_LIST_VIEW}
          isSearching={searchQuery !== ''}
        />
        {patientsCount[statusFilter] > rowsPerPage && (
          <Box display='flex' justifyContent='center' mt={5}>
            <Pagination
              count={Math.ceil(patientsCount[statusFilter] / rowsPerPage)}
              page={page}
              onChange={(_, value) => setPage(value)}
              shape='circular'
              siblingCount={1}
              boundaryCount={1}
            />
          </Box>
        )}
      </Box>
    </RefetchPatientsCtx.Provider>
  );
}
